import React, { useContext, useRef } from 'react'
import useAsyncEffect from 'shared/hooks/use-async-effect'
import Layer from '.'
import { CreationToolsArtboardContext } from '../context'
import { ArtboardBaseLayerProps, ArtboardDragPosition, ArtboardTextProps } from '../types'
import { getCanvasTextFillStyle, getWrappedCanvasText } from '../utils'

interface Props extends ArtboardBaseLayerProps, ArtboardTextProps {
    maxWidth: number
    forceSelected?: boolean
    onMove?: (position: ArtboardDragPosition) => void
}

const CreationToolsArtboardTextLayer = ({
    defaultX,
    defaultY,
    defaultRotation = 0,
    forceSelected,
    resizeable,
    rotateable,
    moveable,
    text,
    color,
    strokeColor,
    fontFamily,
    fontSize,
    fontWeight = 'normal',
    lineHeight = 1.3,
    maxWidth,
    onMove,
}: Props) => {
    const { canvasScale } = useContext(CreationToolsArtboardContext)
    const canvasRef = useRef<HTMLCanvasElement>(document.createElement('canvas'))

    useAsyncEffect(async () => {
        if (!canvasRef.current) return

        const canvas = canvasRef.current
        const context = canvas.getContext('2d')

        // preload font
        if (document && (document as any).fonts) {
            await (document as any).fonts.load(`${fontSize}px ${fontFamily}`)
        }

        if (context) {
            const textFontSize = Math.round(fontSize * canvasScale)
            const textMaxWidth = Math.round(maxWidth * canvasScale)
            const textLineHeight = Math.round(textFontSize * lineHeight)
            const textPadding = Math.round(fontSize / 2) // add some padding to the sides

            // set font for measurement in getWrappedCanvasText()
            context.font = `${fontWeight} ${textFontSize}px ${fontFamily}`

            // get wrapped text lines
            const textLines = getWrappedCanvasText(text, context, textMaxWidth)

            // auto size canvas to match text size
            canvas.width = (textLines.length > 1 ? textMaxWidth : context.measureText(text).width) + textPadding * 2
            canvas.height = textLineHeight * textLines.length + textPadding * 2

            // set text styles
            context.textAlign = 'center'
            context.textBaseline = 'middle'
            context.font = `${fontWeight} ${textFontSize}px ${fontFamily}` // this is needed twice since resizing canvas will wipe context styles
            context.fillStyle = getCanvasTextFillStyle(color, context)

            // set stroke styles
            if (strokeColor) {
                context.lineWidth = Math.round(textFontSize * 0.2)
                context.lineCap = 'round'
                context.lineJoin = 'round'
                context.strokeStyle = getCanvasTextFillStyle(strokeColor, context)
            }

            // draw text
            textLines.forEach((line, i) => {
                const lineX = canvas.width / 2
                const lineY = i * textLineHeight + textLineHeight / 2 + textPadding

                if (strokeColor) {
                    context.strokeText(line, lineX, lineY)
                }
                context.fillText(line, lineX, lineY)
            })
        }
    }, [canvasRef, canvasScale, text, color, strokeColor, fontWeight, fontSize, fontFamily, lineHeight, maxWidth])

    if (!canvasRef.current || !text) {
        return null
    }

    return (
        <Layer
            {...{
                frame: canvasRef.current,
                width: canvasRef.current.width / canvasScale,
                height: canvasRef.current.height / canvasScale,
                defaultX,
                defaultY,
                defaultRotation,
                forceSelected,
                resizeable,
                rotateable,
                moveable,
                onMove,
            }}
        />
    )
}

export default CreationToolsArtboardTextLayer
